import React, { useMemo } from 'react';
import { useAds } from '../../components/context/AdsContext';

/**
 * Step 4: Campaign Configuration Component
 * 
 * This component renders the final campaign configuration step of the campaign wizard.
 * Users can set:
 * - Campaign name (auto-generated with platform-type-date format)
 * - Daily budget with currency
 * - Start and end dates
 * - Target locations
 * 
 * The component uses currentCampaign as the source of data and updates it directly.
 * 
 * @param {Object} props - Component props
 * @param {string} props.platform - Selected platform
 * @param {Object} props.campaignTypeObject - Selected campaign type object
 * @param {Function} props.onCampaignUpdate - Callback to update campaign data
 * @param {string} props.defaultCurrency - Default currency for the platform
 * @param {Object} props.currentCampaign - Campaign data from API
 * @param {Object} props.chatSession - Chat session data
 * @param {boolean} props.isEditingCampaign - Whether campaign is being edited
 * @param {string} props.editError - Error message from campaign editing
 * @returns {JSX.Element} Campaign configuration interface
 */

const Step4CampaignConfig = ({ platform, defaultCurrency, currentCampaign, isEditingCampaign, editError, setHasUnsavedChanges, pendingChanges, setPendingChanges }) => {
  
  // Get available pixels from active Meta account
  const { activeMetaAccount } = useAds();
  const availablePixels = activeMetaAccount?.pixels?.data || [];

  // Extract configuration data from currentCampaign
  const addDays = (d, days) => {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + days);
    return nd;
  };

  // Date formatting utilities
  const formatDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const config = useMemo(() => {
    if (!currentCampaign) {
      // Default values when no campaign exists
      return null;
    }
    
    // Merge ad_content properly - if pendingChanges has ad_content, merge it with currentCampaign.ad_content
    const mergedAdContent = pendingChanges.ad_content 
      ? { ...currentCampaign.ad_content, ...pendingChanges.ad_content }
      : currentCampaign.ad_content;
    
    const configData = {
      name: pendingChanges.name !== undefined ? pendingChanges.name : currentCampaign.name,
      budget: pendingChanges.budget_amount_micros !== undefined 
        ? pendingChanges.budget_amount_micros / 1000000 
        : (currentCampaign.budget_amount_micros / 1000000),
      currency: pendingChanges.currency !== undefined ? pendingChanges.currency : currentCampaign.currency,
      startDate: pendingChanges.start_date !== undefined ? pendingChanges.start_date : currentCampaign.start_date,
      endDate: pendingChanges.end_date !== undefined ? pendingChanges.end_date : currentCampaign.end_date,
      locations: pendingChanges.targeting?.locations !== undefined 
        ? pendingChanges.targeting.locations 
        : currentCampaign.targeting?.locations,
      meta_settings: pendingChanges.meta_settings !== undefined 
        ? pendingChanges.meta_settings 
        : currentCampaign.meta_settings,
      ad_content: mergedAdContent
    };
    
    return configData;
  }, [currentCampaign, pendingChanges]);

  // Handle local changes (don't save immediately)
  const handleLocalChange = (updates) => {
    console.log('handleLocalChange called with:', updates);
    setPendingChanges(prev => {
      const newChanges = { ...prev, ...updates };
      console.log('New pendingChanges:', newChanges);
      return newChanges;
    });
    setHasUnsavedChanges(true);
  };


  // Handle start date change with automatic end date adjustment
  const handleStartDateChange = (startDate) => {
    const start = new Date(startDate);
    const currentEnd = new Date(config?.endDate || '');
    
    // If new start date is after current end date, extend end date by 30 days
    const endDate = currentEnd < start ? formatDate(addDays(start, 30)) : (config?.endDate || '');
    
    handleLocalChange({
      start_date: startDate,
      end_date: endDate
    });
  };

  // Handle location addition
  const handleLocationAdd = (e) => {
    if (e.key === 'Enter') {
      const value = e.currentTarget.value.trim();
      if (value) {
        const currentLocations = config?.locations || [];
        const newLocations = Array.from(new Set([...currentLocations, value]));
        handleLocalChange({
          targeting: {
            locations: newLocations
          }
        });
        e.currentTarget.value = '';
      }
    }
  };

  // Handle location removal
  const handleLocationRemove = (index) => {
    const currentLocations = config?.locations || [];
    const newLocations = currentLocations.filter((_, i) => i !== index);
    handleLocalChange({
      targeting: {
        locations: newLocations
      }
    });
  };

  return (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl border border-white/20 shadow-xl p-8">
      {/* Campaign editing status */}
      {isEditingCampaign && (
        <div className="mb-6 flex items-center gap-3 p-4 bg-gradient-to-r from-blue-900/30 to-blue-800/30 border border-blue-700/50 rounded-xl">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
          <span className="text-sm font-medium text-blue-300">Guardando cambios...</span>
        </div>
      )}

      {editError && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-900/30 to-red-800/30 border border-red-700/50 rounded-xl">
          <p className="text-sm font-medium text-red-300">
            <strong>Error:</strong> {editError}
          </p>
        </div>
      )}

      {/* Simple Header */}
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-white">Configuración Final</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Campaign Name */}
        <div className="group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <label className="text-sm font-medium text-white/80">Nombre de la campaña</label>
          </div>
          <input
            value={config?.name || ''}
            onChange={(e) => handleLocalChange({ name: e.target.value })}
            className="w-full border border-white/30 bg-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all duration-200"
            placeholder="Plataforma-Tipo-Fecha"
          />
        </div>

        {/* Daily Budget */}
        <div className="group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <label className="text-sm font-medium text-white/80">Presupuesto diario</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              step={0.1}
              value={config?.budget || 1}
              onChange={(e) => {
                const budgetValue = parseFloat(e.target.value) || 0;
                handleLocalChange({
                  budget_amount_micros: Math.round(budgetValue * 1000000)
                });
              }}
              className="flex-1 border border-white/30 rounded-lg px-3 py-2 text-sm bg-slate-700/50 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all duration-200"
            />
            <div className="px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-sm font-medium text-white/90 min-w-[60px] text-center">
              {config?.currency || defaultCurrency || 'USD'}
            </div>
          </div>
        </div>

        {/* Start Date */}
        <div className="group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <label className="text-sm font-medium text-white/80">Fecha de inicio</label>
          </div>
          <input
            type="date"
            value={config?.startDate || ''}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="w-full border border-white/30 rounded-lg px-3 py-2 text-sm bg-slate-700/50 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all duration-200"
          />
        </div>

        {/* End Date */}
        <div className="group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <label className="text-sm font-medium text-white/80">Fecha de finalización</label>
          </div>
          <input
            type="date"
            value={config?.endDate || ''}
            onChange={(e) => handleLocalChange({
              end_date: e.target.value
            })}
            className="w-full border border-white/30 rounded-lg px-3 py-2 text-sm bg-slate-700/50 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all duration-200"
          />
        </div>

        {/* URL de Destino */}
        <div className="group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <label className="text-sm font-medium text-white/80">URL de destino</label>
            <div className="relative group">
              <svg className="w-4 h-4 text-white/60 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                URL donde los usuarios serán dirigidos al hacer clic
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
          <input
            type="url"
            value={config?.ad_content?.final_url || ''}
            onChange={(e) => {
              const url = e.target.value;
              handleLocalChange({
                ad_content: {
                  ...currentCampaign?.ad_content,
                  final_url: url
                }
              });
            }}
            className="w-full border border-white/30 bg-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all duration-200"
            placeholder="https://tu-sitio-web.com"
          />
        </div>

        {/* Pixel Selection for Meta Campaigns */}
        {platform === 'meta' && availablePixels.length > 0 && (
          <div className="group">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <label className="text-sm font-medium text-white/80">Píxel de Meta</label>
              <div className="relative group">
                <svg className="w-4 h-4 text-white/60 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Selecciona el píxel que se utilizará para rastrear conversiones
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
            <select
              value={config?.meta_settings?.promoted_object?.pixel_id || ''}
              onChange={(e) => {
                const pixelId = e.target.value;
                handleLocalChange({
                  meta_settings: {
                    ...config?.meta_settings,
                    promoted_object: {
                      ...config?.meta_settings?.promoted_object,
                      pixel_id: pixelId
                    },
                    advantage_plus: {
                      ...config?.meta_settings?.advantage_plus,
                      pixel_id: pixelId
                    }
                  }
                });
              }}
              className="w-full border border-white/30 bg-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all duration-200"
            >
              <option value="">Selecciona un píxel</option>
              {availablePixels.map((pixel) => (
                <option key={pixel.id} value={pixel.id}>
                  {pixel.name} ({pixel.event_to_use})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Locations */}
        <div className="md:col-span-2 group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
              <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <label className="text-sm font-medium text-white/80">Ubicación</label>
          </div>
          <div className="border border-white/30 rounded-lg px-3 py-2 bg-slate-700/50 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/50 transition-all duration-200">
            <div className="flex flex-wrap gap-2 mb-2">
              {(config?.locations || []).map((loc, idx) => (
                <span 
                  key={`${loc}-${idx}`} 
                  className="text-xs bg-blue-900/50 text-blue-200 px-2 py-1 rounded-md border border-blue-700/50 flex items-center gap-1"
                >
                  {loc}
                  <button 
                    className="text-blue-300 hover:text-blue-100 hover:bg-blue-800/50 rounded p-0.5 transition-colors" 
                    onClick={() => handleLocationRemove(idx)}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              placeholder="Agregar ciudad o país y Enter"
              className="w-full outline-none text-sm bg-transparent text-white placeholder:text-white/50"
              onKeyDown={handleLocationAdd}
            />
          </div>
        </div>
      </div>


    </div>
  );
};

export default Step4CampaignConfig;
